export function renderMarkdown(source: string): string {
  const text = source.replace(/\r\n/g, '\n');

  const blocks: string[] = [];
  const withCodeBlocks = text.replace(/```(\w*)\n([\s\S]*?)```/g, (_match, lang: string, code: string) => {
    const blockIndex = blocks.length;
    const codeClass = lang ? ` class="language-${escapeHtml(lang)}"` : '';
    blocks.push(`<pre><code${codeClass}>${escapeHtml(code.replace(/\n$/, ''))}</code></pre>`);
    return `\u0000BLOCK${blockIndex}\u0000`;
  });

  const lines = withCodeBlocks.split('\n');
  const html: string[] = [];
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];
    const blockMatch = line.match(/^\u0000BLOCK(\d+)\u0000$/);

    if (blockMatch) {
      html.push(blocks[Number.parseInt(blockMatch[1], 10)]);
      lineIndex += 1;
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      html.push(`<h${level}>${renderInline(headingMatch[2])}</h${level}>`);
      lineIndex += 1;
      continue;
    }

    if (line.includes('|') && lineIndex + 1 < lines.length && /^\|?[\s-:|]+\|?$/.test(lines[lineIndex + 1])) {
      const tableLines: string[] = [];
      while (lineIndex < lines.length && lines[lineIndex].includes('|')) {
        tableLines.push(lines[lineIndex]);
        lineIndex += 1;
      }
      html.push(renderTable(tableLines));
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (lineIndex < lines.length && /^\s*[-*]\s+/.test(lines[lineIndex])) {
        items.push(renderInline(lines[lineIndex].replace(/^\s*[-*]\s+/, '')));
        lineIndex += 1;
      }
      html.push(`<ul>${items.map((item) => `<li>${item}</li>`).join('')}</ul>`);
      continue;
    }

    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = [];
      while (lineIndex < lines.length && /^\s*\d+[.)]\s+/.test(lines[lineIndex])) {
        items.push(renderInline(lines[lineIndex].replace(/^\s*\d+[.)]\s+/, '')));
        lineIndex += 1;
      }
      html.push(`<ol>${items.map((item) => `<li>${item}</li>`).join('')}</ol>`);
      continue;
    }

    if (line.trim() === '') {
      lineIndex += 1;
      continue;
    }

    const paragraphLines = [line];
    lineIndex += 1;
    while (lineIndex < lines.length && lines[lineIndex].trim() !== '') {
      if (/^(#{1,6})\s+/.test(lines[lineIndex])) {
        break;
      }
      paragraphLines.push(lines[lineIndex]);
      lineIndex += 1;
    }

    html.push(`<p>${paragraphLines.map((paragraphLine) => renderInline(paragraphLine)).join('<br>')}</p>`);
  }

  return html.join('\n');
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderInline(value: string): string {
  let rendered = escapeHtml(value);
  rendered = rendered.replace(/`([^`]+)`/g, '<code>$1</code>');
  rendered = rendered.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  rendered = rendered.replace(/\*(.+?)\*/g, '<em>$1</em>');
  rendered = rendered.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noreferrer">$1</a>',
  );
  return rendered;
}

function renderTable(lines: string[]): string {
  const parseLine = (line: string) =>
    line
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((cell) => cell.trim());

  const headers = parseLine(lines[0]);
  const rows = lines.slice(2).map(parseLine);

  const headHtml = headers.map((cell) => `<th>${renderInline(cell)}</th>`).join('');
  const bodyHtml = rows
    .map((row) => `<tr>${row.map((cell) => `<td>${renderInline(cell)}</td>`).join('')}</tr>`)
    .join('');

  return `<table><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`;
}
